/**
 * D-EZ-110 — THE LEAGUE AS THE CHAPTERS READ IT.
 *
 * A chapter reads the same `HistorySeason[]` every story screen reads, plus —
 * where the season's winter was settled by this build's engine — the rest of
 * the `OffseasonResult` a `CareerSeason` already carries (the garage after the
 * winter, the rookies, the sold charters, the re-rates). Those extra fields are
 * optional: a season without them is still told, just with fewer stories.
 *
 * Nothing here is stored and nothing is decided. Every helper is a fold.
 */

import type { DriverCard } from '../types.ts';
import type { SeasonStandings } from '../standings.ts';
import { buildRegistry, sortedSeasons, type DriverRegistry, type HistorySeason } from '../history/index.ts';
import { leagueKeyOf, peopleContext, type PeopleContext } from '../people/context.ts';

/** The winter's results, as far as a chapter reads them. A settled `CareerSeason`'s `offseason` fits. */
export interface ChapterOffseason {
  roster: readonly DriverCard[];
  demotions: readonly { driverId: string; reason: string; name?: string; rank?: number; finishPct?: number | null }[];
  retirements?: readonly { driverId: string; age: number; name?: string; rank?: number; wins?: number; seasons?: number }[];
  carry?: NonNullable<HistorySeason['offseason']>['carry'];
  /** Next season's garage. */
  teams?: readonly { teamId: string; name: string; driverIds: readonly string[] }[];
  rookies?: readonly { card: DriverCard }[];
  teamFailures?: readonly {
    teamId: string;
    name: string;
    balanceMb: number;
    replacedBy?: { teamId: string; name: string };
    driverIds: readonly string[];
  }[];
  rerates?: readonly {
    driverId: string;
    name: string;
    before: DriverCard;
    after: DriverCard;
    aging?: { age: number; kind: 'growth' | 'decline' | 'none'; gas: number; escape: number };
  }[];
  seasonsRun?: Record<string, number>;
}

export type ChapterSeason = Omit<HistorySeason, 'offseason'> & { offseason?: ChapterOffseason };

export function asHistory(seasons: readonly ChapterSeason[]): HistorySeason[] {
  return seasons as unknown as HistorySeason[];
}

/** Seasons up to and including `n`; with `winter: false` the winter after `n` is not known. */
export function seasonsThrough(input: readonly ChapterSeason[], n: number, opts: { winter: boolean }): ChapterSeason[] {
  const out: ChapterSeason[] = [];
  for (const s of sortedSeasons(asHistory(input)) as unknown as ChapterSeason[]) {
    if (s.seasonNumber > n) continue;
    if (s.seasonNumber === n && !opts.winter) out.push({ ...s, offseason: undefined });
    else out.push(s);
  }
  return out;
}

/** A charter keeps its identity when it is bought (`tm-04` → `tm-04-s3`). */
export function teamBase(teamId: string | null | undefined): string | null {
  if (!teamId) return null;
  return teamId.replace(/(-s\d+)+$/, '');
}

export function teamOfIn(
  teams: readonly { teamId: string; name: string; driverIds: readonly string[] }[] | undefined,
  driverId: string,
): { teamId: string; name: string } | null {
  for (const t of teams ?? []) if (t.driverIds.includes(driverId)) return { teamId: t.teamId, name: t.name };
  return null;
}

/** The shop he drove for in season `s` (the season's own garage, then his card). */
export function seasonTeam(s: ChapterSeason, driverId: string): { teamId: string; name: string } | null {
  const t = teamOfIn(s.teams, driverId);
  if (t) return t;
  const stat = s.season.finalStandings.byDriver[driverId];
  const id = stat?.teamId ?? s.roster.find((c) => c.driverId === driverId)?.teamId ?? null;
  if (!id) return null;
  const name = s.teams.find((x) => x.teamId === id)?.name;
  return name ? { teamId: id, name } : null;
}

/**
 * How many seasons in a row, ending with season `through`, he raced for the
 * shop whose charter is `teamId` (sold charters count as the same shop).
 */
export function tenure(registry: DriverRegistry, driverId: string, teamId: string, through: number): number {
  const e = registry.byId[driverId];
  if (!e) return 0;
  const base = teamBase(teamId);
  const lines = e.bySeason.filter((l) => l.seasonNumber <= through).sort((a, b) => b.seasonNumber - a.seasonNumber);
  let n = 0;
  let expect = through;
  for (const l of lines) {
    if (l.seasonNumber !== expect || teamBase(l.teamId) !== base) break;
    n += 1;
    expect -= 1;
  }
  return n;
}

/** One reading of the league as of a moment, shared by a chapter's parts. */
export interface ChapterContext {
  seasons: ChapterSeason[];
  registry: DriverRegistry;
  people: PeopleContext;
  leagueKey: string;
}

/**
 * `whole` is the league as a whole (any cut of it): its first season names the
 * league, so a man's personality is the same whichever moment is being read.
 */
export function chapterContext(seasons: readonly ChapterSeason[], whole: readonly ChapterSeason[] = seasons): ChapterContext {
  const hs = asHistory(seasons);
  const leagueKey = leagueKeyOf(asHistory(whole.length > 0 ? whole : seasons));
  const people = peopleContext({ archive: hs, leagueKey });
  return {
    seasons: [...seasons],
    registry: buildRegistry(hs),
    people,
    leagueKey,
  };
}

/**
 * The championship table after race `n` of season `s` — off the race's own
 * fold, never the final table (that would know the rest of the year). The final
 * table only stands in for the last race of a settled season.
 */
export function standingsAfter(s: ChapterSeason, n: number): SeasonStandings | null {
  const race = s.season.races.find((r) => r.raceNumber === n);
  if (race?.standings && race.standings.drivers.length > 0) return race.standings;
  const last = s.season.races[s.season.races.length - 1];
  if (s.complete !== false && last && last.raceNumber === n && s.season.championId !== undefined) {
    return s.season.pointsStandings ?? s.season.finalStandings;
  }
  return null;
}

/** A name from anywhere in the league (rookies who have not raced included). */
export function nameIn(seasons: readonly ChapterSeason[], driverId: string): string {
  for (let i = seasons.length - 1; i >= 0; i--) {
    const s = seasons[i];
    const c = s.offseason?.roster.find((x) => x.driverId === driverId) ?? s.roster.find((x) => x.driverId === driverId);
    if (c) return c.name;
    const st = s.season.finalStandings.byDriver[driverId];
    if (st) return st.name;
  }
  return driverId;
}
